import {
  buildModelInvocation,
  buildOpenRouterModelId,
  isKnownProfileInput,
  resolveBundledInput,
  resolveModelIdentity,
} from "@x12i/ai-profiles";
import type { ModelInvocationEndpoint } from "@x12i/ai-profiles";
import { TEXT_CATALOG_LANE } from "../../profiles/catalogLane.js";
import type { AiModelRecord } from "../../models/types.js";
import { ModelNameResolver } from "./ModelNameResolver.js";
import { PROVIDER_INFERENCE_MAP } from "./constants.js";
import { normalizeProvider, normalizeString } from "./normalize.js";
import type {
  ModelResolutionInput,
  ModelResolutionResult,
  ModelResolverOptions,
} from "./types.js";

function isOpenRouterProvider(provider?: string): boolean {
  return normalizeProvider(provider) === "openrouter";
}

function buildVendorResolveAttempts(model: string): ModelResolutionInput[] {
  const attempts: ModelResolutionInput[] = [];
  const seen = new Set<string>();
  const add = (m: string, p?: string) => {
    const key = `${p ?? ""}\0${normalizeString(m)}`;
    if (seen.has(key)) return;
    seen.add(key);
    attempts.push({ model: m, provider: p });
  };

  add(model, undefined);

  const normalised = normalizeString(model);
  if (normalised.includes("/")) {
    const prefix = normalised.split("/")[0]!;
    if (!isOpenRouterProvider(prefix)) {
      add(model, normalizeProvider(prefix));
    }
  }

  add(model, "openrouter");
  return attempts;
}

export type ModelVendorRef = {
  provider: string;
  model: string;
};

export type ResolveModelVendorOptions = ModelResolverOptions & {
  /** Return OpenRouter transport shape: provider `openrouter`, model `vendor/slug`. */
  asOpenRouter?: boolean;
};

function endpointToRef(endpoint: ModelInvocationEndpoint): ModelVendorRef {
  return { provider: endpoint.provider, model: endpoint.modelId };
}

function invocationToRef(
  providerHint: string,
  modelId: string,
  options?: ResolveModelVendorOptions,
): ModelVendorRef {
  const invocation = buildModelInvocation(providerHint, normalizeString(modelId));
  if (options?.asOpenRouter) {
    return endpointToRef(
      invocation.openrouter ?? {
        provider: "openrouter",
        modelId: buildOpenRouterModelId(providerHint, modelId),
      },
    );
  }
  return endpointToRef(invocation.direct);
}

function fromAiProfilesIdentity(
  ref: NonNullable<ReturnType<typeof resolveModelIdentity>>,
  options?: ResolveModelVendorOptions,
): ModelVendorRef {
  if (options?.asOpenRouter && ref.invocation?.openrouter) {
    return endpointToRef(ref.invocation.openrouter);
  }
  if (options?.asOpenRouter) {
    return {
      provider: "openrouter",
      model: buildOpenRouterModelId(ref.provider, ref.modelId),
    };
  }
  return { provider: ref.provider, model: ref.modelId };
}

function modelVendorRefFromProfile(
  model: string,
  options?: ResolveModelVendorOptions,
): ModelVendorRef | null {
  try {
    const resolved = resolveBundledInput(model.trim(), {
      catalogLane: options?.catalogLane ?? TEXT_CATALOG_LANE,
    });
    const direct = resolved.invocation?.direct;
    const openrouter = resolved.invocation?.openrouter;
    if (options?.asOpenRouter) {
      if (openrouter) return endpointToRef(openrouter);
      if (direct) {
        return {
          provider: "openrouter",
          model: buildOpenRouterModelId(direct.provider, direct.modelId),
        };
      }
    }
    if (direct) return endpointToRef(direct);
    return {
      provider: resolved.provider,
      model: resolved.modelId.includes("/")
        ? resolved.modelId.slice(resolved.modelId.indexOf("/") + 1)
        : resolved.modelId,
    };
  } catch {
    return null;
  }
}

/**
 * Exact registry / bundled-catalog lookup via @x12i/ai-profiles.
 *
 * Order: known profile/choice → resolveModelIdentity. Does not invent vendor
 * refs from arbitrary `prefix/rest` strings (that would mis-handle profiles).
 */
export function modelVendorRefFromIdentity(
  model: string,
  options?: ResolveModelVendorOptions,
): ModelVendorRef | null {
  const trimmed = model.trim();
  if (!trimmed) return null;

  if (isKnownProfileInput(trimmed)) {
    return modelVendorRefFromProfile(trimmed, options);
  }

  const identity = resolveModelIdentity(trimmed, {
    asOpenRouter: options?.asOpenRouter,
  });
  if (identity) return fromAiProfilesIdentity(identity, options);

  return null;
}

/** Build `{ provider, model }` using @x12i/ai-profiles transport mapping. */
export function modelVendorRefFromVendorAndSlug(
  providerHint: string,
  modelId: string,
  options?: ResolveModelVendorOptions,
): ModelVendorRef {
  return invocationToRef(providerHint, modelId, options);
}

function inferProviderFromSlug(
  slug: string,
  options?: ResolveModelVendorOptions,
): string | undefined {
  const patterns = [
    ...(options?.additionalProviderPatterns ?? []),
    ...PROVIDER_INFERENCE_MAP,
  ];
  for (const { pattern, provider } of patterns) {
    if (pattern.test(slug)) return provider;
  }
  return undefined;
}

function providerHintForModelId(
  modelId: string,
  record: AiModelRecord | null | undefined,
  options?: ResolveModelVendorOptions,
): string | undefined {
  if (record?.providerId) {
    const vendor = normalizeProvider(record.providerId);
    if (vendor && !isOpenRouterProvider(vendor)) return vendor;
  }

  const normalised = normalizeString(modelId);
  if (normalised.includes("/")) {
    const prefix = normalised.split("/")[0]!;
    if (!isOpenRouterProvider(prefix)) {
      return normalizeProvider(prefix) ?? prefix;
    }
  }

  return inferProviderFromSlug(normalised, options);
}

function formatModelVendorRef(
  modelId: string,
  record: AiModelRecord | null | undefined,
  options?: ResolveModelVendorOptions,
): ModelVendorRef | null {
  const hint = providerHintForModelId(modelId, record, options);
  if (!hint) return null;
  return invocationToRef(hint, modelId, options);
}

/** Pattern inference when registry and catalog both miss. */
export function resolveModelVendorLastResort(
  model: string,
  options?: ResolveModelVendorOptions,
): ModelVendorRef | null {
  if (isKnownProfileInput(model.trim())) return null;
  const hint = providerHintForModelId(model, null, options);
  if (!hint) return null;
  return invocationToRef(hint, model, options);
}

/** Map a fuzzy catalog resolution result to `{ provider, model }`. */
export function resolveModelVendorFromResolution(
  result: ModelResolutionResult,
  inputModel: string,
  options?: ResolveModelVendorOptions,
): ModelVendorRef | null {
  if (result.found) {
    const ref = formatModelVendorRef(result.modelId, result.record, options);
    if (ref) return ref;
  }

  if (!result.found && result.bestRejectedCandidate) {
    const ref = formatModelVendorRef(result.bestRejectedCandidate.modelId, null, options);
    if (ref) return ref;
  }

  return resolveModelVendorLastResort(inputModel, options);
}

/** Best-effort vendor lookup from a model name alone (sync, catalog map only). */
export function resolveModelVendorSync(
  model: string,
  catalog: Map<string, AiModelRecord>,
  options?: ResolveModelVendorOptions,
): ModelVendorRef | null {
  const exact = modelVendorRefFromIdentity(model, options);
  if (exact) return exact;

  const resolver = new ModelNameResolver(catalog, options);
  const attempts = buildVendorResolveAttempts(model);
  let last: ModelResolutionResult | null = null;

  for (const attempt of attempts) {
    const result = resolver.resolve(attempt);
    last = result;
    if (result.found) {
      const ref = resolveModelVendorFromResolution(result, model, options);
      if (ref) return ref;
    }
  }

  return resolveModelVendorFromResolution(
    last ?? {
      found: false,
      modelId: null,
      record: null,
      attemptedStrategies: [],
      reason: "Model not found in catalog",
    },
    model,
    options,
  );
}
